import React, { useMemo, useRef, useState, useEffect } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  AnimatePresence,
} from 'motion/react';
import { RsvpForm } from '@/src/modules/rsvp';
import type { InvitationData } from '@/src/modules/templates/types';
import { resolvePlotPlaneWeddingData } from './resolvePlotPlaneWeddingData';
import type { PlotPlaneThemeConfig } from './plotPlaneTypes';
import './plotPlane.css';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);
  return reduced;
}

function HeroParticles({
  kind,
  active,
}: {
  kind: PlotPlaneThemeConfig['particle'];
  active: boolean;
}) {
  if (kind === 'none' || !active) return null;
  const count = 14;
  const items = useMemo(() => {
    return Array.from({ length: count }, (_, i) => ({
      id: i,
      left: `${8 + ((i * 17) % 84)}%`,
      delay: `${(i * 0.55) % 6}s`,
      size: kind === 'marigold-petals' ? 8 + (i % 4) * 2 : 3 + (i % 3),
      tone:
        kind === 'marigold-petals'
          ? 'rgba(245, 158, 11, 0.75)'
          : 'rgba(212, 175, 55, 0.85)',
    }));
  }, [kind]);

  return (
    <div className="plot-plane-particles" aria-hidden="true">
      {items.map((p) => (
        <span
          key={p.id}
          className="plot-plane-particle"
          style={{
            left: p.left,
            bottom: `${6 + (p.id % 5) * 4}%`,
            width: p.size,
            height: kind === 'marigold-petals' ? p.size * 1.35 : p.size,
            background: p.tone,
            animationDelay: p.delay,
            borderRadius: kind === 'marigold-petals' ? '40% 60% 55% 45%' : '999px',
          }}
        />
      ))}
    </div>
  );
}

function BeatPanel({
  theme,
  beatIndex,
  content,
}: {
  theme: PlotPlaneThemeConfig;
  beatIndex: number;
  content: ReturnType<typeof resolvePlotPlaneWeddingData>;
}) {
  const beat = theme.beats[beatIndex];
  if (!beat) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={beat.id}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        transition={{ duration: 0.35, ease: 'easeOut' }}
        className="plot-plane-panel"
      >
        <p
          className="text-[0.62rem] font-semibold uppercase tracking-[0.28em]"
          style={{ color: theme.palette.textMuted }}
        >
          {beat.navLabel}
        </p>

        {beatIndex === 0 && (
          <>
            <h1
              className="plot-plane-display mt-2 text-3xl sm:text-4xl font-semibold"
              style={{ color: theme.palette.gold }}
            >
              {content.coupleLine}
            </h1>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: theme.palette.text }}>
              {content.customMessage}
            </p>
          </>
        )}

        {beatIndex === 1 && (
          <>
            <h2 className="plot-plane-display mt-2 text-2xl font-semibold">
              {beat.id.includes('ganesh') ? 'Shree Ganesh' : 'Courtyard welcome'}
            </h2>
            <p className="mt-2 text-sm leading-relaxed" style={{ color: theme.palette.text }}>
              {beat.id.includes('ganesh')
                ? 'With blessings for an auspicious beginning — we begin our journey together.'
                : content.customMessage}
            </p>
            {!beat.id.includes('ganesh') && (
              <div className="mt-3 rounded-lg border px-3 py-2" style={{ borderColor: theme.palette.panelBorder }}>
                <p className="text-xs uppercase tracking-widest" style={{ color: theme.palette.textMuted }}>
                  Save the date
                </p>
                <p className="plot-plane-display mt-1 text-lg">{content.dateLong}</p>
                <p className="mt-1 text-sm">{content.venue}</p>
              </div>
            )}
          </>
        )}

        {beatIndex === 2 && (
          <>
            <h2 className="plot-plane-display mt-2 text-2xl font-semibold">Sacred mandap</h2>
            <p className="mt-2 text-sm leading-relaxed">
              Join us as we take seven steps around the sacred fire — a promise of partnership and grace.
            </p>
            <div className="plot-plane-arch mt-3">
              {content.couplePhotoUrl ? (
                <img src={content.couplePhotoUrl} alt="" />
              ) : (
                <div className="plot-plane-placeholder">Couple photo</div>
              )}
            </div>
          </>
        )}

        {beatIndex === 3 && (
          <>
            <h2 className="plot-plane-display mt-2 text-2xl font-semibold">Celebrations</h2>
            <p className="mt-1 text-sm" style={{ color: theme.palette.textMuted }}>
              Mehndi · Haldi · Sangeet · Reception
            </p>
            <div className="plot-plane-chip-row">
              {content.functions.map((fn) => (
                <span key={fn.key} className="plot-plane-chip">
                  {fn.label}
                  {fn.time ? ` · ${fn.time}` : ''}
                </span>
              ))}
            </div>
          </>
        )}

        {beatIndex === 4 && (
          <>
            <h2 className="plot-plane-display mt-2 text-2xl font-semibold">RSVP durbar</h2>
            <p className="mt-1 text-sm">{content.hostName}</p>
            <div className="mt-3 rounded-lg border p-3" style={{ borderColor: theme.palette.panelBorder }}>
              <RsvpForm
                invitationId={content.invitationId}
                isLive={content.isLive}
                externalLink={null}
              />
            </div>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function ReducedMotionStory({
  theme,
  content,
}: {
  theme: PlotPlaneThemeConfig;
  content: ReturnType<typeof resolvePlotPlaneWeddingData>;
}) {
  return (
    <div className="plot-plane-fallback">
      {theme.beats.map((beat, idx) => (
        <section key={beat.id} aria-labelledby={`beat-${beat.id}`}>
          <p id={`beat-${beat.id}`} className="text-xs uppercase tracking-[0.25em]" style={{ color: theme.palette.textMuted }}>
            {beat.navLabel}
          </p>
          <BeatPanel theme={theme} beatIndex={idx} content={content} />
        </section>
      ))}
    </div>
  );
}

export function PlotPlaneWeddingExperience({
  data,
  theme,
}: {
  data: InvitationData;
  theme: PlotPlaneThemeConfig;
}) {
  const content = useMemo(() => resolvePlotPlaneWeddingData(data), [data]);
  const scrollRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();
  const beatCount = theme.beats.length;
  const [activeBeat, setActiveBeat] = useState(0);

  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ['start start', 'end end'],
  });

  const cameraZ = useTransform(scrollYProgress, [0, 1], [0, -(beatCount - 1) * 520]);
  const worldTransform = useTransform(cameraZ, (z) => `translate3d(0, 0, ${z}px)`);
  const heroActive = activeBeat === 0;

  useMotionValueEvent(scrollYProgress, 'change', (v) => {
    const idx = Math.min(beatCount - 1, Math.max(0, Math.floor(v * beatCount)));
    setActiveBeat(idx);
  });

  const cssVars = {
    '--pp-bg': theme.palette.bg,
    '--pp-bg-deep': theme.palette.bgDeep,
    '--pp-text': theme.palette.text,
    '--pp-text-muted': theme.palette.textMuted,
    '--pp-accent': theme.palette.accent,
    '--pp-gold': theme.palette.gold,
    '--pp-panel': theme.palette.panel,
    '--pp-panel-border': theme.palette.panelBorder,
    '--pp-font-display': theme.typography.display,
  } as React.CSSProperties;

  const skipToRsvp = () => {
    scrollRef.current?.scrollTo({
      top: scrollRef.current.scrollHeight,
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <main
      className={`plot-plane-root min-h-screen ${reducedMotion ? 'plot-plane-reduced' : ''}`}
      style={cssVars}
    >
      <button type="button" className="plot-plane-skip" onClick={skipToRsvp}>
        Skip to RSVP
      </button>

      {reducedMotion ? (
        <ReducedMotionStory theme={theme} content={content} />
      ) : (
        <div
          ref={scrollRef}
          className="plot-plane-scroll"
          style={{ height: `${beatCount * 115}vh` }}
        >
          <div className="plot-plane-sticky">
            <HeroParticles kind={theme.particle} active={heroActive} />

            <div className="plot-plane-scene" aria-hidden="true">
              <motion.div className="plot-plane-world" style={{ transform: worldTransform }}>
                {theme.beats.map((beat) => (
                  <div
                    key={beat.id}
                    className="plot-plane-layer"
                    style={{
                      transform: `translate3d(-50%, -50%, ${-beat.depth}px)`,
                      ['--pp-layer-art' as string]: beat.backdrop
                        ? `url(${beat.backdrop})`
                        : undefined,
                    }}
                  />
                ))}
              </motion.div>
            </div>

            <div className="plot-plane-hud">
              <div className="plot-plane-hud-inner">
                <div className="plot-plane-progress" aria-hidden="true">
                  {theme.beats.map((b, i) => (
                    <span
                      key={b.id}
                      className={`plot-plane-dot ${i === activeBeat ? 'is-active' : ''}`}
                    />
                  ))}
                </div>
                <BeatPanel theme={theme} beatIndex={activeBeat} content={content} />
                <p className="mt-3 text-[0.65rem]" style={{ color: theme.palette.textMuted }}>
                  Scroll to journey through each courtyard · Partner watermark safe zone below
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
